import { useState } from 'react';
import { ArrowDown2, Cross, Spinner } from '@/components/icons';
import { BackgroundWorkNotice } from '@/components/system';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Progress } from '@/components/ui/progress';
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button';
import { cn } from '@/lib/utils';
import { getBatchProgress, STATUS_META, withEllipsis } from './import-status';
import type { PublisherResultImport } from '@/types/publisher-result-import';

type PublisherResultProcessingPanelProps = {
    imports: PublisherResultImport[];
    cancellingId: string | null;
    onCancel: (record: PublisherResultImport) => void;
    showIllustration: boolean;
};

const CancelImportButton = ({
    record,
    cancellingId,
    onCancel,
}: {
    record: PublisherResultImport;
    cancellingId: string | null;
    onCancel: (record: PublisherResultImport) => void;
}) => {
    const cancelling = cancellingId === record.id;

    return (
        <TooltipIconButton
            label="Cancelar importação"
            onClick={() => onCancel(record)}
            disabled={cancelling}
            className="shrink-0 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
            {cancelling ? (
                <Spinner className="size-4 animate-spin" />
            ) : (
                <Cross className="size-4" />
            )}
        </TooltipIconButton>
    );
};

/**
 * The waiting room for imports still moving through extraction.
 *
 * Everything here is arranged around one message — the work continues without
 * you — so the per-file detail is folded away behind a disclosure and only a
 * single aggregate bar stays on screen. The bar is stage-derived, not timed:
 * it steps forward when the backend reports a new stage and holds still
 * otherwise, which is why a slow stage reads as a pause rather than a stall.
 */
export function PublisherResultProcessingPanel({
    imports,
    cancellingId,
    onCancel,
    showIllustration,
}: PublisherResultProcessingPanelProps) {
    const [filesOpen, setFilesOpen] = useState(false);

    if (imports.length === 0) return null;

    const percent = getBatchProgress(imports);
    const isSingle = imports.length === 1;
    const [firstImport] = imports;

    return (
        <div className="space-y-5">
            {showIllustration && (
                <BackgroundWorkNotice description="O processamento continua em segundo plano e os resultados ficam disponíveis para revisão assim que terminarem." />
            )}

            <div className="space-y-2">
                {/* Filename and stage read as one label, so the percentage on
                    the right centres against the pair rather than the top line. */}
                <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground">
                            {isSingle
                                ? firstImport.original_filename
                                : `Processando ${imports.length} arquivos`}
                        </p>
                        {isSingle && (
                            <p className="truncate text-sm text-muted-foreground">
                                {withEllipsis(STATUS_META[firstImport.status].description)}
                            </p>
                        )}
                    </div>
                    <span className="shrink-0 text-sm tabular-nums text-muted-foreground">
                        {percent}%
                    </span>
                </div>

                <div className="flex items-center gap-3">
                    <Progress value={percent} className="h-1.5 flex-1" />
                    {isSingle && (
                        <CancelImportButton
                            record={firstImport}
                            cancellingId={cancellingId}
                            onCancel={onCancel}
                        />
                    )}
                </div>
            </div>

            {!isSingle && (
                <Collapsible open={filesOpen} onOpenChange={setFilesOpen}>
                    <CollapsibleTrigger className="mx-auto flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm text-muted-foreground transition-colors hover:text-foreground">
                        {filesOpen ? 'Ocultar arquivos' : 'Ver arquivos'}
                        <ArrowDown2
                            className={cn('size-4 transition-transform', filesOpen && 'rotate-180')}
                        />
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <ul className="mt-2 divide-y divide-border/70 border-t border-border/70">
                            {imports.map((record) => (
                                <li key={record.id} className="flex items-center gap-3 py-2">
                                    <Spinner className="size-4 shrink-0 animate-spin text-info" />
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate text-sm text-foreground">
                                            {record.original_filename}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {STATUS_META[record.status].label}
                                        </p>
                                    </div>
                                    <CancelImportButton
                                        record={record}
                                        cancellingId={cancellingId}
                                        onCancel={onCancel}
                                    />
                                </li>
                            ))}
                        </ul>
                    </CollapsibleContent>
                </Collapsible>
            )}
        </div>
    );
}
